import { FC, useCallback, useMemo } from 'react';

import {
  BetOnAllControl,
  ChipValueType,
  HelpChapterAutoplay,
  HelpChapterDisconnectionPolicyInstantBets,
  HelpChapterGameInconveniences,
  HelpChapterNavigationControls,
  HelpChapterPlacingBets,
  HelpChapterPlacingBetsConfiguredProps,
  HelpChapterSubtitles,
  HelpComponentWrapper,
  HelpConfigType,
  IHelpChapter,
  L12nText,
  getHelpChapterTranslations,
  useChipSlice,
  useConfigSlice,
  useGameStateSlice,
  useIsLargerThanWide,
  useIsPortrait,
  useSettingsSlice,
} from '@pp/core';

import {
  HelpChapterBetsAndPayouts,
  HelpChapterGameResult,
  HelpChapterHowToPlay,
  HelpChapterIntroduction,
  HelpChapterLastWinningSymbols,
  HelpChapterReturnToPlayer,
  HelpChapterWheelValidSpinRule,
} from './chapters/index';

import { BetOnAllControlDecorator } from '../gameGrid';
import styles from '../gameGrid/betOnAllControl/BetOnAllControlDecorator.module.css';
import { BetOnAll } from '../gameGrid/betSpot/BetOnAll';
import { useIsPortraitMax } from '../../hooks/useWindowDimension';

export const helpConfig: HelpConfigType = {
  introduction: () => <HelpChapterIntroduction content={getHelpChapterTranslations('introduction')} />,
  howToPlay: () => <HelpChapterHowToPlay content={getHelpChapterTranslations('howToPlay')} isStorybook={false} />,
  betsAndPayouts: () => <HelpChapterBetsAndPayouts content={getHelpChapterTranslations('betsAndPayouts')} />,
  returnToPlayer: () => <HelpChapterReturnToPlayer content={getHelpChapterTranslations('returnToPlayer')} />,

  lastWinningSymbols: () => (
    <HelpChapterLastWinningSymbols content={getHelpChapterTranslations('lastWinningSymbols')} />
  ),

  placingBets: () => <HelpChapterPlacingBetsConfigured content={getHelpChapterTranslations('placingBets')} />,
  autoplay: () => <HelpChapterAutoplay content={getHelpChapterTranslations('autoplay')} />,
  gameResult: () => <HelpChapterGameResult content={getHelpChapterTranslations('gameResult')} />,

  navigationControls: () => (
    <HelpChapterNavigationControls content={getHelpChapterTranslations('navigationControls')} />
  ),

  subtitles: () => <HelpChapterSubtitles content={getHelpChapterTranslations('subtitles')} />,

  validMWSpinRule: () => <HelpChapterWheelValidSpinRule content={getHelpChapterTranslations('validMWSpinRule')} />,

  gameInconveniences: () => (
    <HelpChapterGameInconveniences content={getHelpChapterTranslations('gameInconveniences')} />
  ),

  disconnectionPolicyinstantBets: () => (
    <HelpChapterDisconnectionPolicyInstantBets content={getHelpChapterTranslations('disconnectionPolicyinstantBets')} />
  ),
};

const useSiblingChips = (chips: ChipValueType[], selectedChip: ChipValueType | null) => {
  const siblingChip = useCallback((position: -1 | 1, chips: ChipValueType[], selectedChip: ChipValueType | null) => {
    if (!selectedChip) return null;

    const chipIndex = chips.indexOf(selectedChip);

    if (position === -1) {
      // Position Left
      if (chipIndex === 0) return chips[chipIndex + 1];
      if (chipIndex === chips.length - 1) return chips[chips.length - 3];
      return chips[chipIndex - 1];
    } else {
      // Position Right
      if (chipIndex === 0) return chips[chipIndex + 2];
      if (chipIndex === chips.length - 1) return chips[chips.length - 2];
      return chips[chipIndex + 1];
    }
  }, []);

  return useMemo(
    () => [siblingChip(-1, chips, selectedChip), siblingChip(1, chips, selectedChip)],
    [siblingChip, chips, selectedChip]
  );
};

export const HelpChapterPlacingBetsConfigured: FC<HelpChapterPlacingBetsConfiguredProps> = ({
  content,
  isStorybook,
}) => {
  const tableBetLimits = useConfigSlice.tableBetLimits();
  const tableName = useConfigSlice.tableConfig()?.tableName ?? '';
  const isWideScreen = useIsLargerThanWide();
  const isPortrait = useIsPortrait();
  const currency = useGameStateSlice.getCurrency();
  const currentLanguage = useSettingsSlice.currentLanguage();
  const isPortraitMax = useIsPortraitMax();

  const chips = useChipSlice.getChips();
  const selectedChip = useChipSlice.getSelectedChip();
  const [previousChip, nextChip] = useSiblingChips(chips, selectedChip);

  return (
    <HelpChapterPlacingBets
      content={content}
      tableName={<L12nText tk={tableName} />}
      tableBetLimits={{ min: tableBetLimits?.min ?? 0.1, max: tableBetLimits?.max ?? 1000 }}
      betOnAllProps={{
        betOnAllButton: !isPortrait || isWideScreen ? (
          <BetOnAllControlDecorator>
            <BetOnAllControl
              disabled={false}
              label={<L12nText tk="BETONALL" />}
              labelClass={`${styles.betOnAllLabel}`}
            />
          </BetOnAllControlDecorator>
        ) : (
          <HelpComponentWrapper
            style={{ height: '85px', width: isPortraitMax ? '270px' : '75.5px' }}
            isOriginalHeight={true}
          >
            {' '}
            <BetOnAll isCollapsed={false} isMax={isPortraitMax}></BetOnAll>{' '}
          </HelpComponentWrapper>
        ),
        betOnAllContent: content?.betOnAllNine as IHelpChapter<string>,
      }}
      isStorybook={isStorybook}
      totalBetHidden={true}
      currency={currency || 'EUR'}
      locale={currentLanguage}
      chipStackProps={{ chips: chips, selectedChip: selectedChip, previousChip: previousChip, nextChip: nextChip }}
    />
  );
};
